/**
 * Fuzzy matching of GeoJSON property keys, used to detect which property
 * holds a sample's ID.
 *
 * Uses Fuse.js for fuzzy string matching to handle typos, abbreviations,
 * and common variations (e.g., "spl id" → "sample_id").
 */

import Fuse from "fuse.js";
// Define the IFuseOptions type locally since we can't import it with require
interface IFuseOptions {
	threshold?: number;
	useExtendedSearch?: boolean;
	includeScore?: boolean;
	ignoreLocation?: boolean;
	minMatchCharLength?: number;
}

/**
 * Default Fuse.js options for fuzzy matching.
 * Tuned for matching short strings like property keys.
 */
const DEFAULT_FUSE_OPTIONS: IFuseOptions = {
	// Lower threshold = more strict matching (0 = exact, 1 = match anything)
	threshold: 0.4,
	// Disable extended search patterns
	useExtendedSearch: false,
	// Include score in results for debugging
	includeScore: true,
	// Ignore location - match anywhere in the string
	ignoreLocation: true,
	// Minimum characters before fuzzy matching kicks in
	minMatchCharLength: 2,
};

// ============================================================================
// Fuse.js Instance Caching
// ============================================================================
// Creating a new Fuse instance rebuilds the search index, which is O(n).
// By caching instances, repeated searches on the same option set are O(1) lookup.

interface FuseCacheEntry<T> {
	fuse: Fuse<T>;
	timestamp: number;
}

const fuseCache = new Map<string, FuseCacheEntry<string>>();
const CACHE_MAX_SIZE = 50;
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Generates a cache key for a set of options.
 * For small option sets, uses full content hash.
 * For large sets, uses a fingerprint to avoid expensive serialization.
 */
function generateCacheKey(options: string[], threshold: number): string {
	if (options.length <= 20) {
		return JSON.stringify(options) + ":" + threshold;
	}
	// For large arrays, use a fingerprint instead of full serialization
	return `${options.length}:${options[0]}:${options[options.length - 1]}:${threshold}`;
}

/**
 * Gets or creates a cached Fuse instance for string options.
 */
function getCachedFuse(options: string[], fuseOptions: IFuseOptions): Fuse<string> {
	const key = generateCacheKey(options, fuseOptions.threshold ?? 0.4);
	const cached = fuseCache.get(key);

	if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
		return cached.fuse;
	}

	const fuse = new Fuse(options, fuseOptions);

	// LRU eviction: remove oldest entry if at capacity
	if (fuseCache.size >= CACHE_MAX_SIZE) {
		const oldestKey = fuseCache.keys().next().value;
		if (oldestKey) fuseCache.delete(oldestKey);
	}

	fuseCache.set(key, { fuse, timestamp: Date.now() });
	return fuse;
}

/**
 * Normalizes a string for comparison by removing spaces, hyphens,
 * underscores and converting to lowercase.
 */
function normalize(str: string): string {
	return str.toLowerCase().replace(/[\s\-_]+/g, "");
}

/**
 * Fuzzy matches an input value against a list of valid options using Fuse.js.
 *
 * Matching priority (in order):
 * 1. Exact match (case-insensitive)
 * 2. Normalized match (spaces, hyphens, underscores removed)
 * 3. Fuse.js fuzzy match (handles typos, abbreviations, etc.)
 *
 * @param inputValue - The value to match (from GeoJSON property)
 * @param options - Valid dropdown options to match against
 * @param fuseOptions - Optional Fuse.js options override
 * @returns The best matching option, or null if no match found
 */
export function fuzzyMatchValue(
	inputValue: string | null | undefined,
	options: string[],
	fuseOptions?: IFuseOptions,
): string | null {
	if (inputValue === null || inputValue === undefined || inputValue === "") {
		return null;
	}

	if (options.length === 0) {
		return null;
	}

	// 1. Exact match (case-insensitive)
	const exactMatch = options.find((opt) => opt.toLowerCase() === inputValue.toLowerCase());
	if (exactMatch) return exactMatch;

	// 2. Normalized match (spaces, hyphens, underscores removed)
	const normalizedInput = normalize(inputValue);
	const normalizedMatch = options.find((opt) => normalize(opt) === normalizedInput);
	if (normalizedMatch) return normalizedMatch;

	// 3. Fuse.js fuzzy match (using cached Fuse instance)
	const effectiveOptions = fuseOptions ?? DEFAULT_FUSE_OPTIONS;
	const fuse = getCachedFuse(options, effectiveOptions);
	const results = fuse.search(inputValue);

	if (results.length > 0 && results[0].score !== undefined) {
		// Only return if score is good enough (lower is better in Fuse.js)
		if (results[0].score <= (fuseOptions?.threshold ?? DEFAULT_FUSE_OPTIONS.threshold!)) {
			return results[0].item;
		}
	}

	// No match - return null (user will select manually in ImportTable)
	return null;
}

// ============================================================================
// Sample ID detection
// ============================================================================

const SAMPLE_ID_KEY_ALIASES = ["id", "sample_id", "spl_id", "spl id", "SampleID", "PointID", "point_id", "SAMPLE_ID"];

/**
 * Given raw GeoJSON properties, find the value that represents sample_id.
 * Uses exact match on "sample_id" first, then fuzzy matching against aliases.
 */
export function fuzzyMatchSample(properties: Record<string, unknown> | null): string | null {
	if (!properties) return null;
	if ("sample_id" in properties && properties.sample_id) return String(properties.sample_id);
	for (const key of Object.keys(properties)) {
		const match = fuzzyMatchValue(key, SAMPLE_ID_KEY_ALIASES, { threshold: 0.3 });
		if (match) return properties[key] ? String(properties[key]) : null;
	}
	return null;
}

/**
 * Detect which property key represents sample_id. Returns the KEY name or null.
 */
export function detectSampleIdKey(properties: Record<string, unknown> | null): string | null {
	if (!properties) return null;
	if ("sample_id" in properties) return "sample_id";
	for (const key of Object.keys(properties)) {
		const match = fuzzyMatchValue(key, SAMPLE_ID_KEY_ALIASES, { threshold: 0.3 });
		if (match) return key;
	}
	return null;
}
